import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useIsOpenedStore } from '../NotificationCenterStore';
import { VARIANTS } from '../constants/variants';
import Content from './Content';
import { useShallow } from 'zustand/shallow';

/**
 * @typedef {object} SectionProps
 * @property {Array<*>} notifications
 * @property {(notificationId: number) => void} handleMarkAsReadClick
 * @property {boolean} [loading]
 * @property {() => void} fetchMoreData
 * @property {boolean} [hasMore]
 * @property {boolean} [showUnreadOnly]
 */

/**
 * @param {SectionProps} props
 */
const Section = props => {
  const { notifications, handleMarkAsReadClick, loading, fetchMoreData, hasMore, showUnreadOnly } =
    props;

  const isOpen = useIsOpenedStore(useShallow(state => state.isOpened));

  const sectionRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      sectionRef.current.scrollTo({
        top: 0,
        left: 0
      });
    }
  }, [isOpen, showUnreadOnly]);

  const handleMarkAsReadAndTriggerScroll = notificationId => {
    handleMarkAsReadClick(notificationId);
    triggerScrollingForElement(sectionRef.current);
  };

  return (
    <AnimatePresence>
      <motion.section
        ref={sectionRef}
        variants={VARIANTS.SECTION_VARIANT}
        animate={isOpen ? 'open' : 'closed'}
        className='notification-section scrollBar'
        id='scrollable-section'
        data-testid='scrollable-section'>
        <Content
          notifications={notifications}
          handleMarkAsReadClick={handleMarkAsReadAndTriggerScroll}
          loading={loading}
          fetchMoreData={fetchMoreData}
          hasMore={hasMore}
        />
      </motion.section>
    </AnimatePresence>
  );
};

const triggerScrollingForElement = element => {
  element.dispatchEvent(new CustomEvent('scroll'));
};

export default Section;
